<template>
  <BaseDialog
    :visible="visible"
    @update:visible="(val) => emit('update:modelValue', val)"
    class="!w-[95%] sm:!w-[90%] md:!w-[50%] !max-w-[900px] h-[80vh] sm:h-[85vh] md:h-[90vh] md:rounded-[16px] rounded-[12px]"
    height="auto"
    :draggable="false"
    margin="20px"
  >
    <div class="flex justify-end items-center pt-4 pr-4">
      <button
        @click="emit('update:modelValue', false)"
        class="cursor-pointer border-none bg-transparent flex justify-center items-center"
        v-html="cancelSvg"
        :aria-label="$t('accessibilityLabels.close')"
        :autofocus="visible ? true : false"
      ></button>
    </div>
    <div class="flex flex-col gap-y-[37px] py-6 sm:py-10 px-4 sm:px-[40px]">
      <div
        class="flex flex-col-reverse sm:flex-row sm:justify-between gap-4 sm:h-[63.7px]"
      >
        <div>
          <p
            class="!font-medium !text-[20px] !font-just !text-btnActive !pb-[6.7px]"
          >
            {{ $t('setPrincipalLabels.enter') }}
          </p>
          <h2 class="!text-deepSky !text-[26.8px] !font-medium !font-just">
            {{ $t('setPrincipalLabels.authenticationCode') }}
          </h2>
        </div>
        <div class="flex justify-center sm:justify-start">
          <div v-html="lvedSvg()"></div>
        </div>
      </div>

      <div class="flex flex-col items-center gap-[13px] w-full">
        <p class="!font-400 !text-[16px] !text-btnActive !font-pop text-center">
          {{ $t('setPrincipalLabels.enter5DigitsCodeReceivedFrom', { name: props.name }) }}
        </p>
        <div class="w-full flex justify-center">
          <div class="w-fit">
            <BaseInputOtp
              v-model="form.token"
              :invalid="validation.token.$error"
              @blur="validation.token.$touch"
              :name="'otp'"
              id="otp"
              :length="otpLength"
            />
          </div>
        </div>

        <p
          v-if="errorMessage"
          class="text-red-500 text-sm font-pop text-center"
        >
          {{ translatedErrorMessage }}
        </p>
      </div>
      <div class="pt-[25px] flex justify-center">
        <BaseButton
          :label="$t('forgotPasswordLabels.validateCode')"
          class="!w-full lg:!w-[280px] lg:!px-0 !text-base !border-0 !font-400"
          type="submit"
          :disabled="loading || !form.token || form.token.length < otpLength"
          :loading="loading"
          :name="$t('forgotPasswordLabels.validateCode')"
          @click="submit"
        />
      </div>
    </div>
  </BaseDialog>
</template>

<script setup>
import { ref, computed, reactive, watch } from "vue";
import { lvedSvg, closeSvg } from "@/utils/commonSvg";
import useVuelidate from "@vuelidate/core";
import { required, maxLength, minLength } from "@vuelidate/validators";
import { useI18n } from "vue-i18n";
import i18n from "@/i18n";

const { t, te } = useI18n();

const fullscreen = ref(true);
const cancelSvg = computed(() =>
  closeSvg({
    height: fullscreen.value ? "32px" : "24px",
    width: fullscreen.value ? "32px" : "24px",
  })
);
const form = reactive({
  token: "",
});
const emit = defineEmits([
  "update:modelValue",
  "validateCode",
  "clearError",
  "update:visible",
]);

const props = defineProps({
  modelValue: Boolean,
  showCancel: {
    type: Boolean,
    default: true,
  },
  loading: Boolean,
  otpLength: {
    type: Number,
    default: 6,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  name: {
    type: String,
  },
});

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

const translatedErrorMessage = computed(() => {
  if (!props.errorMessage) return "";
  
  const errorMsg = props.errorMessage.trim();
  
  // Try different translation key formats
  // 1. Direct key (e.g., "api_error_agent_verify_code_mismatch")
  if (te(`validation.apiErrorMessages.${errorMsg}`)) {
    const translated = t(`validation.apiErrorMessages.${errorMsg}`);
    return translated;
  }
  
  // 2. Check if it's already a full path
  if (te(errorMsg)) {
    const translated = t(errorMsg);
    return translated;
  }
  
  // 3. Try to find the key by matching the English text
  const errorMessagesMap = {
    "codes did not match": "api_error_agent_verify_code_mismatch",
    "order invalid, the user must begin the process and then the agent verifies": "api_error_agent_verify_order",
    "Invalid code": "api_error_agent_verify_code_mismatch"
  };
  
  const matchedKey = errorMessagesMap[errorMsg];
  if (matchedKey && te(`validation.apiErrorMessages.${matchedKey}`)) {
    const translated = t(`validation.apiErrorMessages.${matchedKey}`);
    return translated;
  }
    return errorMsg;
});

const rules = computed(() => {
  return {
    token: {
      required,
      maxLength: maxLength(props.otpLength),
      minLength: minLength(props.otpLength),
    },
  };
});

const validation = useVuelidate(rules, form);

watch(
  () => form.token,
  () => {
    if (props.errorMessage) {
      emit("clearError");
    }
  }
);

// Clear form when dialog closes
watch(
  () => props.modelValue,
  (newVal) => {
    if (!newVal) {
      form.token = "";
      validation.value.$reset();
    }
  }
);

const submit = () => {
  validation.value.$touch();
  if (!validation.value.$error) {
    emit("validateCode", form.token);
  }
};
</script>
